<i18n lang="yaml">
  pt:
    minimum-allowed-size: "menores que 1MB"
    we-recommend-file-of: "Recomendamos arquivos"
    search-your-files: "procure no seu computador"
    drag-file: "Arraste seu arquivo aqui ou"
</i18n>

<template>
  <div class="uploader border-radius-2 mb-2 cursor-pointer">
    <input
      :id="`drop-${componentId}`"
      type="file"
      :name="name"
      class="d-none"
      :multiple="multiple"
      @change="setFilePreview($event)"
    >

    <label
      :for="`drop-${componentId}`"
      class="cursor-pointer drop-file-label"
      @dragover="
        $dropStyle(
          $event,
          'label.drop-file-label',
          'drop-zone--over',
          true
        )
      "
      @dragleave="
        $dropStyle(
          $event,
          'label.drop-file-label',
          'drop-zone--over',
          false
        )
      "
      @dragend="
        $dropStyle(
          $event,
          'label.drop-file-label',
          'drop-zone--over',
          false
        )
      "
      @drop="dropFileLocal($event, `#drop-${componentId}`)"
    >
      <slot
        v-if="!file"
        name="info"
      >
        <div
          data-grid="column"
          class="uploader__wrapper border-radius-1 grid--va-center grid--center"
        >
          <strong class="uploader__infotext text-center">
            {{ $t('drag-file') }} <span class="strong">{{ $t('search-your-files') }}</span>.
          </strong>

          <span class="uploader__tips text-center mt-2">
            {{ $t('we-recommend-file-of') }} {{ $t('minimum-allowed-size') }}.
          </span>
        </div>
      </slot>

      <div
        v-else
        data-grid="column"
        class="uploader__wrapper border-radius-1 grid--va-center grid--center p-3"
      >
        <span
          data-grid
          class="remove p-absolute"
          @click.prevent="emptyFile"
        >
          <XIcon />
        </span>

        <img
          class="image"
          :src="file"
          alt="Image"
        >
      </div>
    </label>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import XIcon from '../icons/XIcon'

export default defineComponent({
  name: 'DropFile',

  components: {
    XIcon
  },

  props: {
    componentId: {
      type: String,
      default: () => ''
    },

    name: {
      type: String,
      default: () => ''
    },

    multiple: {
      type: Boolean,
      default: () => false
    },

    fileValue: {
      type: String,
      default: () => ''
    }
  },

  emits: ['list-files'],

  data: () => ({
    file: ''
  }),

  watch: {
    fileValue (f) {
      this.file = f
    }
  },

  mounted () {
    if (this.fileValue) {
      this.file = this.fileValue
    }

    this.emitter.on('remove-file-from-list', this.removeFileFromList)
  },

  unmounted () {
    this.emitter.off('remove-file-from-list', this.removeFileFromList)
  },

  methods: {
    emptyFile () {
      this.file = ''
      document.querySelector(`#drop-${this.componentId}`).value = ''
    },

    setFilePreview (e) {
      const files = this.getListFiles(e.target.files)
      this.$emit('list-files', files || [])
    },

    dropFileLocal (ev, inp) {
      const files = this.getListFiles(this.$dropFile(ev, inp))
      this.$emit('list-files', files)

      this.$dropStyle(
        ev,
        'label.drop-file-label',
        'drop-zone--over',
        false
      )
    },

    getListFiles (files) {
      const files_ = []

      if (!this.multiple) {
        this.file = URL.createObjectURL(files[0])

        return
      }

      Object.entries(files || {}).forEach((file) => {
        files_.push({
          name: file[1].name || '',
          type: file[1].type,
          url: file[1].type.includes('image') ? URL.createObjectURL(file[1]) : ''
        })
      })

      return files_ || []
    },

    removeFileFromList (index) {
      const dt = new DataTransfer()

      const input = document.getElementById(`drop-${this.componentId}`)

      const { files } = input

      for (let i = 0, len = files.length; i < len; i++) {
        const file = files[i]

        if (index !== i) dt.items.add(file)
      }

      input.files = dt.files
    }
  }
})
</script>

<style lang="scss" scoped>
  .uploader {
    --borderColor: rgb(var(--gray2));

    padding: 18px;
    width: 448px;
    height: 239px;
    border: dashed 2px var(--borderColor);

    .drop-zone--over {
      .uploader__wrapper {
        border: 1px solid var(--primary);
      }
    }

    &__preview {
      height: 100%;
      width: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    &__wrapper {
      --backgroundColor: rgb(var(--gray4));

      width: 100%;
      height: 100%;
      background-color: var(--backgroundColor);
      position: relative;

      svg {
        width: 39px;
        height: 39px;
        color: rgb(var(--gray2));
      }

      .remove {
        right: 4px;
        top: 4px;
        width: max-content;

        svg {
          width: 15px;
          height: 15px;
          stroke: var(--error);
        }
      }

      .image {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    &__tips,
    &__infotext {
      --fontColor: var(--black1);

      color: var(--fontColor);
    }

    &__infotext {
      max-width: 259px;
      font: 400 1.6rem / 1.25 "Nunito", sans-serif;

      span {
        font-weight: 700;
        color: var(--primary);
      }
    }

    &__tips {
      max-width: 340px;
      font: 400 1.3rem / 1.25 "Nunito", sans-serif;
    }

    .is-dark-theme & {
      --borderColor: rgb(var(--gray8));

      &__wrapper {
        --backgroundColor: rgb(var(--gray8));
      }

      &__tips,
      &__infotext {
        --fontColor: rgb(var(--gray4));
      }
    }
  }
</style>
